import React, { useState, useLayoutEffect, useEffect, useRef } from 'react';
import { HashLink } from 'react-router-hash-link';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react';
import NavSectionButton from 'components/NavSection/NavSectionButton';
import { useLocation } from 'react-router-dom';
import { useStores } from 'util/mobx/stores';

const SurgeryGuideNavigation = observer(() => {
  const location = useLocation();
  const currentPath = `${location.pathname}${location.search}`;
  const { t } = useTranslation('briefing');
  const { surgeryGuideStore, surgeryStore, domainStore } = useStores();
  const [size, setSize] = useState({ width: 0, height: 0 });
  const [isProcedureChapterActive, setIsProcedureChapterActive] = useState(false);
  const itemRef: React.RefObject<HTMLDivElement> = useRef(null);

  useLayoutEffect(() => {
    function updateSize() {
      setSize({ width: window.innerWidth, height: window.innerHeight });
    }
    window.addEventListener('resize', updateSize);
    updateSize();
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  useEffect(() => {
    /**
     * closes the item menu
     * @param event
     */
    const onClose = (event: MouseEvent) => {
      if (size.width <= 991 && isProcedureChapterActive && event.target instanceof Node && !itemRef.current?.contains(event.target)) {
        setIsProcedureChapterActive(false);
      }
    };

    const onScrollClose = (ev: Event) => {
      if (size.width <= 991 && isProcedureChapterActive && ev.target instanceof Node && !itemRef.current?.contains(ev.target)) {
        setIsProcedureChapterActive(false);
      }
    };

    if (itemRef.current) {
      if (isProcedureChapterActive) {
        document.body.addEventListener('click', onClose);
        document.body.addEventListener('scroll', onScrollClose, true);
      } else {
        document.body.removeEventListener('click', onClose);
        document.body.removeEventListener('scroll', onScrollClose, false);
      }
    }

    return () => {
      document.body.removeEventListener('click', onClose);
      document.body.removeEventListener('scroll', onScrollClose, false);
    };
  }, [itemRef, isProcedureChapterActive, size.width]);

  const handleOpenPlaner = () => surgeryStore.setIsPlannerDrawerOpen(true);
  const handleOpenDrawer = () => surgeryGuideStore.setIsProcedureDrawerOpen(true);

  const { shortName: functionalAreaShortName } = domainStore.currentFunctionalArea;

  return (
    <>
      <div className="txt_section_nav for_mobile">{t('menu.headline')}</div>
      <NavSectionButton
        icon="icon_list-bullet_16"
        label={t('menu.materialPlan')}
        routePath={`/surgery-guide/materials?surgeryGuideId=${surgeryGuideStore.surgeryGuide?.surgeryGuideId}`}
        currentPath={currentPath}
      />
      <div className="nav_chapter">
        <div onClick={() => setIsProcedureChapterActive(true)}>
          <NavSectionButton
            icon="icon_filter-check_16"
            label={t('menu.procedurePlan')}
            routePath={`/surgery-guide/procedures?surgeryGuideId=${surgeryGuideStore.surgeryGuide?.surgeryGuideId}`}
            currentPath={currentPath}
          />
        </div>
        {location.pathname === '/surgery-guide/procedures' && (size.width > 991 || (size.width <= 991 && isProcedureChapterActive)) && (
          <div className="chapter" style={{ display: 'flex' }} ref={itemRef}>
            {surgeryGuideStore.groupedGuideProcedures.map(procedureItem => (
              <HashLink
                to={`/surgery-guide/procedures?surgeryGuideId=${surgeryGuideStore.surgeryGuide?.surgeryGuideId}#${procedureItem.chapter}`}
                smooth
                className="chapter_link w-inline-block"
                key={procedureItem.chapter}
                replace
                onClick={() => setIsProcedureChapterActive(false)}
              >
                <div className="text-block-8">{procedureItem.chapter}</div>
              </HashLink>
            ))}
          </div>
        )}
      </div>
      <NavSectionButton
        onClick={handleOpenPlaner}
        className="btn_section auto_width mobile_only"
        icon="icon_calendar-date-2_16"
        label={t('menu.openPlaner', { functionalAreaShortName })}
      />
      {location.pathname === '/surgery-guide/procedures' && (
        <NavSectionButton
          disabled={surgeryGuideStore.checkableGuideProcedures.length === 0}
          onClick={handleOpenDrawer}
          className="btn_section auto_width mobile_only"
          icon="icon_check-e_16_grey"
          label={t('menu.openCheck')}
        />
      )}
    </>
  );
});

export default SurgeryGuideNavigation;
